const KEY_PATTERN = /^[A-Za-z0-9_]+$/;
const DATA_X_ATTR_PATTERN = /^data-x-(.+)$/;
const MAX_KEY_LENGTH = 255;
const BLOCKED_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

function createMetadataRecord(): Record<string, string> {
  return Object.create(null) as Record<string, string>;
}

let store = createMetadataRecord();

/**
 * HTML lowercases attribute names (`data-x-clickId` → `data-x-clickid`).
 * Normalize SDK keys the same way so add/remove match script-seeded entries.
 *
 * Prefer snake_case for multi-word keys (`click_id` / `data-x-click_id`):
 * camelCase in HTML cannot preserve word boundaries (`clickId` → `clickid`).
 */
function normalizeMetadataKey(key: string): string {
  return key.toLowerCase();
}

function isValidKey(key: unknown): key is string {
  if (typeof key !== 'string') return false;
  const normalized = normalizeMetadataKey(key);
  if (BLOCKED_KEYS.has(normalized)) return false;
  return (
    normalized.length > 0 &&
    normalized.length <= MAX_KEY_LENGTH &&
    KEY_PATTERN.test(normalized)
  );
}

function isValidValue(value: unknown): value is string {
  return typeof value === 'string';
}

export function parseDataXAttributesFromElement(element: Element): Record<string, string> {
  const result = createMetadataRecord();
  for (let i = 0; i < element.attributes.length; i++) {
    const attr = element.attributes[i];
    const match = attr.name.match(DATA_X_ATTR_PATTERN);
    if (match && isValidKey(match[1])) {
      result[normalizeMetadataKey(match[1])] = attr.value;
    }
  }
  return result;
}

export function addMeta(key: string, value: string): void {
  if (!isValidKey(key) || !isValidValue(value)) return;

  store[normalizeMetadataKey(key)] = value;
}

export function removeMeta(key: string): void {
  if (!isValidKey(key)) return;
  delete store[normalizeMetadataKey(key)];
}

export function getMetadataSnapshot(): Record<string, string> {
  return { ...store };
}

export function seedFromAttributes(attributes: Record<string, string>): void {
  for (const [key, value] of Object.entries(attributes)) {
    addMeta(key, value);
  }
}

export function seedMetadataFromScriptAttributes(script: Element): void {
  seedFromAttributes(parseDataXAttributesFromElement(script));
}

/** @internal Test-only reset */
export function resetMetadataStoreForTests(): void {
  store = createMetadataRecord();
}
